import Vue from 'vue';
import Vuex from 'vuex';
import { get } from 'lodash';
import http from './utils/http';
import { abilitiesPlugin } from '@casl/vue';
import { version } from '../../package';
import appAbility from './utils/appAbility';
import activities from './modules/activities';
import auditLogs from './modules/auditLogs';
import authUser from './modules/authUser';
import spaces from './modules/spaces';
import space from './modules/space';
import devices from './modules/devices';
import device from './modules/device';
import user from './modules/user';
import users from './modules/users';
import notifier from './modules/notifier';
import venue from './modules/venue';
import channelMaps from './modules/channelMaps';
import tag from './modules/tag';
import tags from './modules/tags';
import settings from './modules/settings';
import support from './modules/support';
import jobs from './modules/jobs';
import serviceAccount from './modules/serviceAccount';
import brand from './modules/brand';
import features from './utils/features';
import localStorageSessionPlugin from './plugins/localStorageSession';
import appFeatures from '@/appFeatures';

Vue.use(Vuex);
Vue.use(abilitiesPlugin, appAbility);

const defaultState = {
    appVersion: version,
    navDrawer: false,
    sidebarItems: [],
    msoLoginUrl: '',
    isDarkMode: false,
    jobsDialogOpen: false,
    earlyAccess: false,
    language: 'en'
};

export const mutations = {
    setNavDrawer(state, value) {
        state.navDrawer = value;
    },
    setSidebarItems(state, items) {
        state.sidebarItems = items;
    },
    updateAppAbilities(state, newRules) {
        if (newRules && newRules.length) {
            const mergedRules = [...appAbility.rules, ...newRules];
            appAbility.update(mergedRules);
        }
    },
    setAppAbilities(state, rules) {
        appAbility.update(rules);
    },
    setMsoLoginUrl(state, value) {
        state.msoLoginUrl = value;
    },
    setIsDarkMode(state, value) {
        state.isDarkMode = value;
    },
    setJobsDialogOpen(state, value) {
        state.jobsDialogOpen = value;
    },
    setEarlyAccess(state, value) {
        state.earlyAccess = value;
    },
    setLanguage(state, value) {
        state.language = value;
    }
};

export const actions = {
    async initStore({ commit }) {
        // call this on the app starts to load the default values
        http.init(get(appFeatures, 'env.VMS_API'));
        commit('setMsoLoginUrl', get(appFeatures, 'env.XERXES_LOGIN_API'));
    },
    buildDataEntityAbilities({ commit, state, dispatch }) {
        // will build the app entities abilities for all
        // data entities based on user permissions
        // can be used as: this.$can('read', 'Device')...
        console.log('calling buildDataEntityAbilities');
        const entityRules = features.buildEntityAbilities(
            state.authUser.userPermissions
        );
        const userRules = features.buildUserInfoAbilities(
            state.authUser.userInfo
        );
        commit('setAppAbilities', [...entityRules, ...userRules]);
        return dispatch('clearTableViews').then(() => {
            dispatch('buildSidebarItems');
        });
    },
    buildSidebarAbilities({ commit, state, getters }) {
        console.log('calling buildSidebarAbilities');
        const sidebarRules = features.buildSidebarAbilities(
            state.authUser.userPermissions,
            getters,
            appFeatures
        );
        commit('updateAppAbilities', sidebarRules);
    },
    buildSidebarItems({ commit, dispatch, state }) {
        // will build the sidebar navigarion menu dynamiacally from appFeatures
        // need to build the sidebar abilities before building the items
        console.log('calling buildSidebarItems');
        return dispatch('buildSidebarAbilities').then(() => {
            let sidebarItems = features.buildSidebarItems(
                appAbility,
                appFeatures
            );
            if (!state.earlyAccess) {
                sidebarItems = sidebarItems.filter(item => !item.earlyAccess);
            }
            commit('setSidebarItems', sidebarItems);
        });
    },
    clearTableViews({ commit }) {
        // when the user changes the venue we need to clear all tableViews fields
        // and let the code rebuild it based on the new permissions
        console.log('calling clearTableViews');
        commit('devices/setTableFields', []);
        commit('spaces/setTableFields', []);
        commit('tags/setTableFields', []);
        commit('activities/setTableFields', []);
        commit('auditLogs/setTableFields', []);
        commit('settings/setTableFields', []);
        commit('jobs/setTableFields', []);
        commit('users/setTableFields', []);
    },
    saveIsDarkMode({ commit }, value) {
        console.log('calling saveIsDarkMode');
        if (typeof value === 'boolean' && value === true) {
            commit('setIsDarkMode', value);
            localStorage.setItem('isDarkMode', JSON.stringify(true));
        } else {
            commit('setIsDarkMode', false);
            localStorage.setItem('isDarkMode', JSON.stringify(false));
        }
    },
    saveEarlyAccess({ commit }, value) {
        console.log('calling saveEarlyAccess');
        if (typeof value === 'boolean' && value === true) {
            commit('setEarlyAccess', value);
            localStorage.setItem('earlyAccess', JSON.stringify(true));
        } else {
            commit('setEarlyAccess', false);
            localStorage.setItem('earlyAccess', JSON.stringify(false));
        }
    },
    showJobsDialog({ commit }) {
        console.log('calling showJobsDialog');
        commit('setJobsDialogOpen', true);
    },
    async exportData(
        { dispatch },
        { entityName, searchParams, filterParams, isDsSearch }
    ) {
        console.log('calling exportData', entityName);
        if (entityName) {
            const exportAction = `${entityName}/fetchItems`;
            const res = await dispatch(exportAction, {
                searchParams,
                filterParams,
                isDsSearch
            });
            if (res && res.data) {
                return res.data.content;
            }
        }
        return [];
    }
};

let store = new Vuex.Store({
    state: defaultState,
    mutations,
    actions,
    modules: {
        activities,
        auditLogs,
        authUser,
        brand,
        channelMaps,
        device,
        devices,
        jobs,
        notifier,
        serviceAccount,
        settings,
        space,
        spaces,
        support,
        tag,
        tags,
        user,
        users,
        venue
    },
    plugins: [localStorageSessionPlugin]
});

// need to hook http notifications to the store mutations
http.requestPending(() => {
    store.dispatch('notifier/requestPending');
});
http.requestStart(() => {
    // this is causing the loader to reset if we have multiple calls
    // so for now we don't do anything in the this hook
});
http.requestComplete(() => {
    store.dispatch('notifier/requestDone');
});
http.responseSuccess(message => {
    store.dispatch('notifier/success', { message: message });
});
http.responseError(message => {
    store.dispatch('notifier/error', { message: message });
});

http.responseUnauthorized(() => {
    store.dispatch('authUser/clearUserToken');
});

export default store;
